/** Enterprise OS primitives — Skeleton · EmptyState · StatusPill · KpiCard. */
import { BrandPattern } from "@/components/brand/BrandPattern";
import { CheckCircle2 } from "lucide-react";

export function Skeleton({ className = "" }) {
  return <div className={"os-skeleton " + className} aria-hidden="true" />;
}

export function SkeletonRows({ rows = 4 }) {
  return (
    <div className="space-y-2.5" data-testid="os-skeleton">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-2.5 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function OsEmpty({ icon: Icon = CheckCircle2, title, description, action, testId, tone = "good", pattern = "brand" }) {
  const toneCls = { good: "text-[color:var(--st-success)] bg-[color:var(--st-success-soft)]",
    neutral: "text-[color:var(--st-text-muted)] bg-[color:var(--st-surface-alt)]",
    info: "text-[color:var(--st-accent)] bg-[color:var(--st-accent-soft)]" }[tone];
  return (
    <div className="relative flex flex-col items-center overflow-hidden text-center py-8 px-4" data-testid={testId}>
      <BrandPattern set={pattern} density="sparse" seed={(title || "").length + 3} />
      <div className={"relative h-10 w-10 rounded-full grid place-items-center mb-3 " + toneCls}>
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </div>
      <div className="relative text-sm font-semibold text-[color:var(--st-text)]">{title}</div>
      {description && <div className="relative text-xs text-[color:var(--st-text-muted)] mt-1 max-w-xs">{description}</div>}
      {action && <div className="relative mt-3">{action}</div>}
    </div>
  );
}

const PILL = {
  good: "os-pill os-pill-good", warn: "os-pill os-pill-warn", critical: "os-pill os-pill-critical",
  info: "os-pill os-pill-info", neutral: "os-pill os-pill-neutral", ai: "os-pill os-pill-ai",
};
export function StatusPill({ tone = "neutral", children, dot = true, testId }) {
  return (
    <span className={PILL[tone] || PILL.neutral} data-testid={testId}>
      {dot && <span className="os-pill-dot" />}
      {children}
    </span>
  );
}

const fmtValue = (v, fmt) => {
  if (v == null) return "—";
  if (fmt === "inr") {
    const n = Number(v);
    if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
    return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  }
  return Number(v).toLocaleString("en-IN");
};

const TONE_BAR = { good: "bg-[color:var(--st-success)]", warn: "bg-[color:var(--st-warn)]",
  critical: "bg-[color:var(--st-critical)]", info: "bg-[color:var(--st-cyan)]", neutral: "bg-[color:var(--st-border-strong)]" };

export function KpiCard({ label, value, fmt, hint, tone = "neutral", onClick, testId, index = 0 }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="os-card os-rise text-left p-4 flex flex-col gap-1 min-w-0 relative overflow-hidden"
      style={{ animationDelay: `${index * 40}ms` }}>
      <span className={"absolute left-0 top-3 bottom-3 w-[3px] rounded-r " + (TONE_BAR[tone] || TONE_BAR.neutral)} />
      <span className="text-[11px] uppercase tracking-[0.12em] text-[color:var(--st-text-muted)] font-medium pl-2">{label}</span>
      <span className="text-2xl font-bold tracking-tight text-[color:var(--st-text)] pl-2 font-display tabular-nums">{fmtValue(value, fmt)}</span>
      {hint && <span className="text-[11px] text-[color:var(--st-text-subtle)] pl-2 truncate">{hint}</span>}
    </button>
  );
}

export { fmtValue };
